import React, { useContext, useMemo, useState } from "react"
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"
import {
    AccountBalanceWallet,
    Apartment,
    CalendarMonth,
    Close,
    ReceiptLong,
    TrendingUp,
} from "@mui/icons-material"
import { DateTime } from "luxon"
import { useQuery } from "react-query"
import { useSnackbar } from "notistack"
import { useNavigate } from "react-router-dom"

import RideLogo from "../../Assets/RIDE_logo.png"
import AuthContext from "../../Context/auth_provider.jsx"
import useAxiosPrivate from "../../Hooks/useAxiosPrivate.js"
import useUserPermissions from "../../Hooks/useUserPermissions.js"
import RestrictedAccess from "../../Component/restrictedAccess.jsx"
import CustomeDatePicker from "../../Component/datePicker.jsx"
import { formatToTwoDecimalPlaces } from "../../Utils/dataFormat.js"

const GOLD = "#c9932b"
const TEAL = "#1a8a8a"
const GREY = "#9e9e9e"
const DARK = "#1c1c1c"

// Report header meta row (Date Range / Program Account)
function MetaRow({ icon, label, value }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 1 }}>
            <Box sx={{ color: DARK, display: "flex" }}>{icon}</Box>
            <Typography sx={{ fontSize: "0.9rem" }}>
                <strong>{label}:</strong> {value}
            </Typography>
        </Box>
    )
}

// Section title with a leading dark circular icon and a trailing rule
function SectionTitle({ icon, title }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 3, mb: 1.5 }}>
            <Avatar sx={{ bgcolor: DARK, color: "#fff", width: 34, height: 34 }}>
                {icon}
            </Avatar>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 700 }}>
                {title}
            </Typography>
            <Divider sx={{ flex: 1, ml: 1 }} />
        </Box>
    )
}

// Trip summary card (Total Spend / Number of Transactions)
function SummaryCard({ icon, label, value }) {
    return (
        <Paper
            variant="outlined"
            sx={{ flex: 1, minWidth: 200, p: 2, borderRadius: "14px" }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: "#f0f0f0", color: DARK, width: 40, height: 40 }}>
                    {icon}
                </Avatar>
                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                    {label}
                </Typography>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, lineHeight: 1.1 }}>
                {value}
            </Typography>
        </Paper>
    )
}

// Day / Month / Year spend column
function PeriodColumn({ label, color, value, emptyText }) {
    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 160,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                px: 1,
            }}
        >
            <CalendarMonth sx={{ color, fontSize: 30 }} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mt: 0.5, mb: 1 }}>{label}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: color }} />
                <Box sx={{ flex: 1, height: 4, bgcolor: color, borderRadius: 2 }} />
            </Box>
            {value !== null ? (
                <Typography sx={{ fontWeight: 800, fontSize: "1rem", mt: 1.5 }}>
                    {value}
                </Typography>
            ) : (
                <Typography
                    sx={{ color: "#6b7280", textAlign: "center", mt: 1.5, fontSize: "0.8rem" }}
                >
                    {emptyText}
                </Typography>
            )}
        </Box>
    )
}

// The report card itself, replicating the corporate reporting design
export function CorporateReportView({ companyName, fromDate, toDate, programs, isFetching }) {
    const from = fromDate ? DateTime.fromISO(fromDate) : null
    const to = toDate ? DateTime.fromISO(toDate) : null

    const { totalAmount, totalTrips } = useMemo(
        () =>
            (programs ?? []).reduce(
                (acc, p) => ({
                    totalAmount: acc.totalAmount + (Number(p?.total_amount) || 0),
                    totalTrips: acc.totalTrips + (Number(p?.total_trips) || 0),
                }),
                { totalAmount: 0, totalTrips: 0 }
            ),
        [programs]
    )

    const money = `ETB ${formatToTwoDecimalPlaces(totalAmount)}`
    const sameUnit = (unit) => !!from && !!to && from.hasSame(to, unit)
    const dateRange =
        from && to
            ? `${from.toFormat("MMM d")} – ${to.toFormat("MMM d, yyyy")}`
            : "--"

    return (
        <Box sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
            {/* Header */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box
                    component="img"
                    src={RideLogo}
                    alt="RIDE Plus"
                    sx={{ height: 60, objectFit: "contain", mb: 1.5 }}
                />
                <Typography
                    sx={{
                        fontSize: { xs: "1.3rem", sm: "1.6rem" },
                        fontWeight: 800,
                        color: DARK,
                        textAlign: "center",
                    }}
                >
                    RIDE Corporate Reporting
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", width: "100%", my: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            bgcolor: DARK,
                            transform: "rotate(45deg)",
                            mx: 1,
                        }}
                    />
                    <Divider sx={{ flex: 1 }} />
                </Box>
            </Box>

            <MetaRow icon={<CalendarMonth />} label="Date Range" value={dateRange} />
            <MetaRow
                icon={<Apartment />}
                label="Program Account"
                value={companyName || "--"}
            />

            {isFetching ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress sx={{ color: DARK }} />
                </Box>
            ) : (
                <>
                    {/* Trip Summary */}
                    <SectionTitle icon={<ReceiptLong />} title="Trip Summary" />
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <SummaryCard
                            icon={<AccountBalanceWallet />}
                            label="Total Spend"
                            value={money}
                        />
                        <SummaryCard
                            icon={<ReceiptLong />}
                            label="Number of Transactions"
                            value={totalTrips.toLocaleString()}
                        />
                    </Box>

                    {/* Spend by Day / Month / Year */}
                    <SectionTitle icon={<TrendingUp />} title="Spend by Day / Month / Year" />
                    <Box sx={{ display: "flex", flexWrap: "wrap", rowGap: 3 }}>
                        <PeriodColumn
                            label="Day"
                            color={GREY}
                            value={sameUnit("day") ? money : null}
                            emptyText="No daily breakdown provided"
                        />
                        <PeriodColumn
                            label="Month"
                            color={TEAL}
                            value={sameUnit("month") ? money : null}
                            emptyText="No monthly breakdown provided"
                        />
                        <PeriodColumn
                            label="Year"
                            color={GOLD}
                            value={sameUnit("year") ? money : null}
                            emptyText="No yearly breakdown provided"
                        />
                    </Box>

                    {/* Programs breakdown */}
                    <SectionTitle icon={<Apartment />} title="Programs" />
                    <Paper variant="outlined" sx={{ borderRadius: "14px", overflow: "hidden" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#f0f0f0" }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Program</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right">
                                        Trips
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right">
                                        Amount (ETB)
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {programs?.length ? (
                                    programs.map((p) => (
                                        <TableRow key={p.program_name}>
                                            <TableCell>{p.program_name}</TableCell>
                                            <TableCell align="right">
                                                {Number(p.total_trips || 0).toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatToTwoDecimalPlaces(p.total_amount)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ color: "#6b7280" }}>
                                            No program activity in this date range
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Paper>

                    {/* Total banner */}
                    <Box
                        sx={{
                            mt: 3,
                            p: { xs: 1.5, sm: 2 },
                            bgcolor: DARK,
                            border: `2px solid ${GOLD}`,
                            borderRadius: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: 2.5,
                        }}
                    >
                        <Avatar sx={{ bgcolor: "#fff", color: DARK, width: 48, height: 48 }}>
                            <AccountBalanceWallet />
                        </Avatar>
                        <Divider orientation="vertical" flexItem sx={{ borderColor: "#555" }} />
                        <Box>
                            <Typography sx={{ color: "#fff", fontSize: "0.95rem", fontWeight: 600 }}>
                                Total Transaction Value:
                            </Typography>
                            <Typography
                                sx={{
                                    color: "#d4a017",
                                    fontSize: { xs: "1.3rem", sm: "1.7rem" },
                                    fontWeight: 800,
                                    lineHeight: 1.1,
                                }}
                            >
                                {money}
                            </Typography>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    )
}

// Corporate programs report generating dialog (route-mounted)
function ProgramsReport() {
    const navigate = useNavigate()
    const { programsReport } = useUserPermissions()
    const { companyId } = useContext(AuthContext)
    const axiosPrivate = useAxiosPrivate()
    const { enqueueSnackbar } = useSnackbar()

    // USESTATE
    const [openGenerated, setOpenGenerated] = useState(false)
    const [fromDate, setFrom] = useState(null)
    const [toDate, setTo] = useState(null)

    const from = fromDate ? DateTime.fromISO(fromDate).toISODate() : null
    const to = toDate ? DateTime.fromISO(toDate).toISODate() : null

    // QUERY - corporate-only endpoint, so bypass the team-aware fetch hooks
    const { data, isFetching } = useQuery(
        ["programs-report", companyId, from, to],
        () =>
            axiosPrivate.get(`/corporate/${companyId}/programs-report`, {
                params: {
                    filter: JSON.stringify([
                        {
                            column_field: "created_at",
                            operator_value: "between",
                            value: `${from}|${to}`,
                        },
                    ]),
                },
            }),
        {
            enabled: !!companyId && !!from && !!to && openGenerated && programsReport,
            onError: (error) => {
                const fieldError =
                    error.response?.data?.error?.field_error?.[0]?.description
                const errorMessage = error.response?.data?.error?.message
                enqueueSnackbar(fieldError || errorMessage || "Request Failed", {
                    variant: "error",
                })
            },
        }
    )

    const report = data?.data?.data

    // EVENT HANDLERS & HELPERS
    const handleClose = () => navigate(-1)

    // RENDER
    if (!programsReport) return <RestrictedAccess />

    return (
        <Dialog
            fullWidth
            maxWidth={openGenerated ? "md" : "sm"}
            open
            onClose={handleClose}
        >
            <DialogTitle
                sx={{
                    backgroundColor: "#FAFAFA",
                    py: 3,
                    boxShadow: "0px 1px 7px #0000001A",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Grid container>
                    <Grid xs={10} lg={10} sm={10} xl={11} md={11}>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: "bold", textAlign: "center" }}
                        >
                            {!openGenerated && "Generate Programs Report"}
                            {openGenerated && isFetching && "Generating..."}
                            {openGenerated && !isFetching && "Programs Report"}
                        </Typography>
                    </Grid>
                    <Grid sx={{ ml: openGenerated ? 1 : 0 }}>
                        {openGenerated && !isFetching ? (
                            <Tooltip title="Close Modal" arrow enterDelay={50}>
                                <IconButton onClick={handleClose} sx={{ color: "#000" }}>
                                    <Close />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </Grid>
                </Grid>
            </DialogTitle>

            <DialogContent
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    my: 1,
                }}
            >
                {openGenerated ? (
                    <CorporateReportView
                        companyName={report?.company_name}
                        fromDate={from}
                        toDate={to}
                        programs={report?.programs}
                        isFetching={isFetching}
                    />
                ) : (
                    <Box sx={{ width: "100%" }}>
                        <Typography sx={{ fontSize: "17px", mt: "1em" }}>
                            Choose date and generate report
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                width: "100%",
                                mb: 4,
                                mt: 3,
                            }}
                        >
                            <CustomeDatePicker
                                label="From"
                                value={fromDate}
                                maxDate={toDate}
                                onChange={(newValue) => setFrom(newValue)}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                width: "100%",
                            }}
                        >
                            <CustomeDatePicker
                                disableFuture
                                label="To"
                                value={toDate}
                                minDate={fromDate}
                                onChange={(newValue) => setTo(newValue)}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    backgroundColor: "#FAFAFA",
                    py: 3,
                    px: 4,
                    display: "flex",
                    justifyContent: "flex-end",
                    boxShadow: "0px 1px 7px #0000001A",
                    borderRadius: "6px",
                }}
            >
                <Button
                    onClick={() => (openGenerated ? setOpenGenerated(false) : handleClose())}
                    size="small"
                    variant="contained"
                    sx={{
                        color: "primary.main",
                        ":hover": { bgcolor: "#e4e4e4" },
                        backgroundColor: "#e4e4e4",
                        textTransform: "none",
                        fontWeight: "bold",
                        width: 120,
                    }}
                >
                    {openGenerated ? "Back" : "Cancel"}
                </Button>
                {!openGenerated && (
                    <>
                        <Box sx={{ px: 1 }} />
                        <Button
                            disabled={!from || !to}
                            variant="contained"
                            sx={{
                                width: 120,
                                fontWeight: "bold",
                                color: "#fff",
                                boxShadow: " 0px 2px 5px #00000033",
                                borderRadius: "8px",
                                textTransform: "capitalize",
                            }}
                            onClick={() => setOpenGenerated(true)}
                        >
                            Generate
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default ProgramsReport
